type GuestSchema = Partial<{
    occupation_id: number;
    occupation_free_input: string;
    prefecture: number | null;
    zip_code: string;
    addr: string;
}>;

type GetLocaleText = (
    tgt: string | Translation,
    ...vars: (string | number)[]
) => string;

export abstract class InvalidError {
    abstract readonly key: string;
    abstract localeInvalidMessage(_: GetLocaleText): string;

    /**
     * HACK:
     * トランスパイルターゲットがES5であるため、Errorの拡張が正しく動作しない
     * そのため継承せずプロパティとしてErrorクラスを持たせている
     *
     * ※チェックインタブレットアプリのver1系の対応方針を決定せずにトランスパイルターゲットの更新をしないこと
     */
    readonly error: Error;
    constructor(message: string) {
        this.error = new Error(message);
    }

    get name() {
        return this.constructor.name;
    }
}

export class RequiredItemIsNothing extends InvalidError {
    constructor(readonly key: string) {
        super(`${key} is required.`);
    }

    localeInvalidMessage(_: GetLocaleText): string {
        return `${_('required')} : ${_(this.key)}`;
    }
}
export class StringLengthShortThanMinimum extends InvalidError {
    constructor(
        readonly key: string,
        readonly minimumLength: number
    ) {
        super(`Length of ${key} is must equal or more than ${minimumLength}.`);
    }

    localeInvalidMessage(_: GetLocaleText): string {
        return _('min_length_alert', _(this.key), this.minimumLength);
    }
}
export class StringLengthLargeThanMaximum extends InvalidError {
    constructor(
        readonly key: string,
        readonly maximumLength: number
    ) {
        super(`Length of ${key} is must equal or less than ${maximumLength}.`);
    }

    localeInvalidMessage(_: GetLocaleText): string {
        return _('max_length_alert', _(this.key), this.maximumLength);
    }
}

export type ItemSetting<G extends GuestSchema> = {
    column_name: keyof G;
    required: boolean;
    minlength?: number;
    maxlength?: number;
};

export default class GuestRegisterHelper<G extends GuestSchema> {
    constructor(readonly items: { readonly [key: string]: ItemSetting<G> }) {}

    *changedColumns(prev: G, next: G): Iterable<keyof G & string> {
        if (prev.prefecture !== next.prefecture) yield 'prefecture';
        for (const k of Object.keys(this.items)) {
            if (k === 'prefecture') continue;
            if (k === 'occupation') {
                if (prev.occupation_id !== next.occupation_id)
                    yield 'occupation_id';
                if (prev.occupation_free_input !== next.occupation_free_input)
                    yield 'occupation_free_input';
                continue;
            }
            const key = k as keyof G & string;
            if (prev[key] !== next[key]) yield key;
        }
    }

    validationAlerts(guest: G, keys: string[], _: GetLocaleText): string[] {
        return keys
            .map((key) => {
                try {
                    this.validateColumn(guest, key);
                } catch (err) {
                    if (err instanceof InvalidError) {
                        return err.localeInvalidMessage(_);
                    } else {
                        console.error(err);
                    }
                }
                return '';
            })
            .filter((k) => k);
    }

    validateColumn(guest: G, key: string) {
        if (key === 'occupation') {
            this.validateOccupation(guest);
            return;
        }

        if (guest.prefecture === 99) {
            if (key === 'zip_code') return;
        }

        const value = guest[key as keyof G];
        const item = this.items[key] ?? {
            column_name: key,
            required: false,
        };

        if (item.required && !value) {
            throw new RequiredItemIsNothing(key);
        }

        if (typeof value == 'string' && value) {
            const minlength = item.minlength ?? 0;
            const maxlength = item.maxlength ?? Infinity;
            if (value.length < minlength)
                throw new StringLengthShortThanMinimum(key, minlength);
            if (value.length > maxlength)
                throw new StringLengthLargeThanMaximum(key, maxlength);
        }
    }

    private validateOccupation(guest: G) {
        const item = this.items.occupation ?? {
            column_name: 'occupation',
            required: false,
        };
        if (item.required && !guest.occupation_id) {
            throw new RequiredItemIsNothing('occupation');
        }

        if (guest.occupation_id !== 99) return;
        const freeInput = guest.occupation_free_input;

        if (item.required && !freeInput) {
            throw new RequiredItemIsNothing('occupation');
        }

        if (typeof freeInput == 'string' && freeInput) {
            const minlength = item.minlength ?? 0;
            const maxlength = item.maxlength ?? Infinity;
            if (freeInput.length < minlength)
                throw new StringLengthShortThanMinimum('occupation', minlength);
            if (freeInput.length > maxlength)
                throw new StringLengthLargeThanMaximum('occupation', maxlength);
        }
    }
}
